import { Component, Event, EventEmitter, Listen, Prop, State, Watch, h } from '@stencil/core';
import { FailureData } from '../../globals/callbacks';
import { Checkout } from '../../globals/checkout';
import { Customer } from '../../globals/customer';
import { FindApprovalInfo } from '../../globals/find-approval-info';
import { ErrorCodes, fullStateAbbreviations, numCCShown } from '../../globals/globals';
import { PageKey } from '../../globals/page-key';
import { SiteConfig } from '../../globals/site-config';
import { getConditions } from '../../globals/terms-and-conditions';
import { ApplyFlowTranslations } from '../../globals/translations';
import { VCard } from '../../globals/vcard';
import { ErrorHelper, getDateLong, pcDataLayer } from '../../helpers';
import { AddressIsBlank, GetNextPage, GetPrevPage } from '../../helpers/flow-helper';
import { CreateApplicationReturn, GenericAPIError, StatusCodeError, createApplication, findApproval, getEsignUrl } from '../../utils';
import { checkout } from '../../utils/checkout';
import { SubmitCartResponse, submitCart } from '../../utils/submit-cart';
import { updateLeaseApplication } from '../../utils/update-lease-application';
import { MERCHANT_BILLED_IDS } from '../../utils/merchant-billed-map';

@Component({
  tag: 'pc-apply-flow',
  styleUrl: 'pc-apply-flow.scss',
  shadow: false,
})
export class PcApplyFlow {
  @State() customer: Customer = {
    preference: { optInToMarketing: false, language: 'en' },
  };

  @State() applyOnly = false;
  @State() currentView: PageKey = PageKey.onboarding;
  @State() exitCancel: PageKey;

  @State() isLoading = false;
  @State() redirectToReview = false;
  @State() addressNotPrefilled = true;

  @State() cartResponse: SubmitCartResponse;
  @State() merchantBilledItems: SubmitCartResponse['items'] = [];
  @State() application: CreateApplicationReturn;
  @State() esignUrl: string;
  @State() errorHelper: ErrorHelper = new ErrorHelper();
  @State() vCard: VCard;
  @State() showExitDialog = false;
  @State() showLoadingDialog = false;

  @Prop({ mutable: true }) checkoutData: Checkout;
  @Prop({ mutable: true }) retailerConfig: Partial<SiteConfig>;
  @Prop() translations: Partial<ApplyFlowTranslations>;
  @Prop() configUIData: Record<string, unknown> & CustomUIConfig = {};

  @Event({ bubbles: true, composed: true }) vCardReceived: EventEmitter;
  @Event({ bubbles: true, composed: true }) launchButtonClick: EventEmitter;

  componentDidUpdate() {
    this.sendGtmPageView(PageKey[this.currentView]);
  }

  componentWillLoad() {
    const { customer } = this.checkoutData;

    this.onCheckoutChange();
    this.sendGtmPageView(PageKey[this.currentView]);
    this.addressNotPrefilled = !customer?.address?.shipping || AddressIsBlank(customer.address.shipping);

    this.errorHelper.hasStateValidError = this.checkValidState(customer?.address?.shipping?.state);
  }

  @Listen('continueClick', { target: 'body' })
  getUpdatedView() {
    this.sendButtonClick(PageKey[this.currentView], 'continue');
    this.stepForward();
  }

  @Listen('exitClick', { target: 'body' })
  exitClick() {
    this.sendButtonClick(PageKey[this.currentView], 'exit');
    this.showExitDialog = true;
  }

  @Listen('exitPromptCancel', { target: 'body' })
  exitPromptCancel() {
    this.showExitDialog = false;
  }

  @Listen('setView', { target: 'body' })
  setUpdatedView(event: CustomEvent) {
    this.sendButtonClick(PageKey[this.currentView], 'continue');

    if (event.detail) {
      const newView = event.detail as PageKey;

      return (this.currentView = newView);
    }
  }

  @Listen('backClick', { target: 'body' })
  stepBack() {
    this.sendButtonClick(PageKey[this.currentView], 'back');
    const prevView = GetPrevPage(this.currentView);

    if (this.redirectToReview) {
      this.currentView = PageKey.review;
      this.redirectToReview = false;
    } else if (this.addressNotPrefilled && this.currentView === PageKey.incomeInfo) {
      this.currentView = PageKey.addressInfo;
    } else if (prevView !== PageKey.none) {
      this.currentView = prevView;
    }
  }

  @Listen('needHelp', { target: 'body' })
  needHelpHandler() {
    this.currentView = PageKey.needHelp;
  }

  @Listen('updateView', { target: 'body' })
  jumpBack(event: CustomEvent) {
    const newView = String(event.detail);

    this.currentView = PageKey[newView];
    this.redirectToReview = true;
  }

  @Listen('updateCustomer', { target: 'body' })
  updateCustomer(event: CustomEvent) {
    const newCustomerData = event.detail as Partial<Customer>;

    this.customer = { ...this.customer, ...newCustomerData };
  }

  @Listen('submitCart', { target: 'body' })
  async submitCart() {
    this.showLoadingDialog = true;

    try {
      const res = await submitCart(this.checkoutData.cart);

      this.sendButtonClick(PageKey[this.currentView], 'continue');
      pcDataLayer.gtmCartSuccess({
        cashPrice: res.cashPrice,
        cartTotalQuantity: res.items.reduce((acc, obj) => {
          return acc + obj.quantity;
        }, 0),
        cartTotalItems: res.items.length,
        cartTotal: res.totalDueAtSigning,
        deliveryCost: this.checkoutData?.cart?.deliveryCost,
      });

      this.cartResponse = res;
      this.currentView = PageKey.cartReview;

      this.merchantBilledItems = res.items.filter(item => MERCHANT_BILLED_IDS.has(item.identifier));

      if (this.cartResponse?.status === 'CART_ABOVE_NONLEASABLE_LIMIT' || this.cartResponse?.status === 'CART_WARRANTY_NOT_ALLOWED') {
        this.currentView = PageKey.paymentSigning;
      }
    } catch (error) {
      this.errorHelper.hasGenericError = true;
      this.currentView = PageKey.approved;
    }
    this.showLoadingDialog = false;
  }

  @Listen('foundApplication', { target: 'body' })
  foundApplication(event: CustomEvent) {
    const applicationResponse = event.detail as CreateApplicationReturn;

    this.application = applicationResponse;
    this.currentView = this.getResumedPage(applicationResponse.status);
  }

  @Listen('submitApplication', { target: 'body' })
  async submitApplication() {
    this.sendButtonClick(PageKey[this.currentView], 'submit application');
    this.showLoadingDialog = true;
    this.errorHelper.resetErrorFlags();
    pcDataLayer.gtmApplicationSubmit();

    try {
      const applicationResponse = await createApplication();

      this.application = applicationResponse;

      pcDataLayer.gtmApplicationResult({
        approvalStatus: applicationResponse.status,
        leaseApprovalAmount: applicationResponse.approvalLimit,
        leaseId: applicationResponse.leaseId,
        customerId: applicationResponse.customerId,
      });

      this.currentView = this.getResumedPage(applicationResponse.status);
    } catch (error) {
      this.currentView = PageKey.disclosures;

      if (error instanceof StatusCodeError) {
        if (error.statusCode === ErrorCodes.ServerError) {
          this.errorHelper.hasUnableSubmitFailureCase = true;

          return;
        }
      }
      this.errorHelper.hasGenericError = true;
    } finally {
      this.showLoadingDialog = false;
    }
  }

  @Watch('checkoutData')
  onCheckoutChange() {
    if (typeof this.checkoutData === 'undefined') {
      return;
    }

    if (typeof this.checkoutData.customer !== 'undefined') {
      const { email, phone, firstName, lastName } = this.checkoutData.customer;

      this.customer = { ...this.customer, ...(firstName && { firstName }), ...(lastName && { lastName }), ...(email && { email }), ...(phone && { phone }) };

      if (typeof this.checkoutData?.customer?.address?.shipping !== 'undefined') {
        const abbrLength = 2;
        const { shipping } = this.checkoutData.customer.address;
        const correctedState = shipping.state.length > abbrLength ? fullStateAbbreviations[shipping.state.toLocaleUpperCase()] : shipping.state;

        const correctedAddress = { ...shipping, state: correctedState };

        this.customer = { ...this.customer, ...(!AddressIsBlank(shipping) && { address: { shipping: correctedAddress, billing: correctedAddress } }) };
      }
    }

    this.applyOnly = this.checkoutData?.merchant?.flowType === 'applyOnly';
  }

  @Listen('launchEsign', { target: 'body' })
  async handleLaunchEsign() {
    this.showLoadingDialog = true;

    try {
      const { esignUrl } = await getEsignUrl();

      this.esignUrl = esignUrl;
      this.currentView = PageKey.esign;
    } catch (error) {
      this.errorHelper.hasGenericError = true;
      this.currentView = PageKey.paymentSigning;
    } finally {
      this.showLoadingDialog = false;
    }
  }

  @Listen('verifyPayment', { target: 'body' })
  async setEsignUrl() {
    this.sendButtonClick(PageKey[this.currentView], PageKey[this.currentView] === 'paymentSigning' ? 'verify payment' : 'I agree');

    const avsEnabled = Boolean(this.retailerConfig?.features?.CheckoutAvsFlow);

    if (avsEnabled && this.currentView === PageKey.paymentSigning && this.merchantBilledItems.length) {
      this.currentView = PageKey.wirelessPlan;
    } else {
      this.showLoadingDialog = true;

      try {
        this.errorHelper.resetErrorFlags();

        const { esignUrl } = await getEsignUrl();

        this.esignUrl = esignUrl;
        this.currentView = PageKey.esign;
      } catch (error) {
        if (error instanceof StatusCodeError) {
          const statusMsg = await error.getErrorStatusMessage();

          this.errorHelper.setCCError(statusMsg);
        } else {
          this.errorHelper.hasGenericError = true;
        }

        this.currentView = PageKey.paymentSigning;
      }
      this.showLoadingDialog = false;
    }
  }

  @Listen('esignComplete', { target: 'body' })
  async esignComplete() {
    this.showLoadingDialog = true;

    try {
      const { cardNumber, expirationDate, cvv, updateDeliveryDateUrl, updateOrderIdUrl, cardUserFirstName, cardUserLastName, billingAddress } = await checkout();

      const customerFullName = `${cardUserFirstName} ${cardUserLastName}`;

      this.vCard = {
        name: customerFullName,
        firstName: cardUserFirstName || this.customer.firstName,
        lastName: cardUserLastName || this.customer.lastName,
        number: cardNumber,
        expDate: expirationDate,
        cvv,
        updateDeliveryDateUrl,
        updateOrderIdUrl,
        leaseId: this.application?.leaseId,
        billingAddress,
      };
      this.vCardReceived.emit({ ...this.vCard, name: customerFullName });

      pcDataLayer.gtmCheckoutComplete();
    } catch (error) {
      if (error instanceof StatusCodeError) {
        if (error.statusCode === ErrorCodes.ServerError) {
          this.errorHelper.hasUnableCheckoutFailureCase = true;
          pcDataLayer.gtmErrorGeneric('CHECKOUT_VCARD_FAILURE', error.message);
          this.handleOnFailure('CHECKOUT_VCARD_FAILURE');

          return;
        }
      }
      this.errorHelper.hasGenericError = true;
    } finally {
      this.showLoadingDialog = false;
      this.currentView = PageKey.checkoutCard;
    }
  }

  @Listen('esignTimeout', { target: 'body' })
  handleEsignTimeout() {
    this.currentView = PageKey.paymentSigning;
  }

  @Listen('addCardClick', { target: 'body' })
  handleAddCardClick() {
    this.currentView = PageKey.addCard;
    this.sendButtonClick(PageKey[this.currentView], 'add card');
    this.errorHelper.resetErrorFlags();
  }

  @Listen('estimatorClick', { target: 'body' })
  estimatorClick() {
    const enableCloseButton = this.configUIData?.LCE?.enableCloseButton;
    const url = `https://cdn.progweb.prod.aws.progleasing.com/lceApp/?storeId=${this.retailerConfig.parentStoreId}&hideFooter=true&enableCloseButton=${enableCloseButton}`;
    this.sendButtonClick(PageKey[this.currentView], 'estimate payments');
    window.open(url, '_blank');
  }

  @Listen('startNewAppClick', { target: 'body' })
  handleStartNewAppClick() {
    this.sendButtonClick(PageKey[this.currentView], 'start new application');
    this.launchButtonClick.emit();
  }

  @Listen('useMyApprovalClick', { target: 'body' })
  handleUseMyApprovalClick() {
    this.errorHelper.resetErrorFlags();
    this.currentView = PageKey.useMyApproval;

    this.sendButtonClick(PageKey[this.currentView], 'use my approval');
    pcDataLayer.gtmApplicationStart({ reapply: true });
  }

  @Listen('updateApprovalInfo', { target: 'body' })
  async handleUpdateApprovalInfoClick(event: CustomEvent) {
    this.sendButtonClick(PageKey[this.currentView], 'find my lease approval');
    this.showLoadingDialog = true;
    this.errorHelper.resetErrorFlags();
    const { lastFourSsn, phoneNumber } = event.detail;

    const approvalSearchInfo = {
      last4Ssn: lastFourSsn,
      phoneNumber: phoneNumber.replace(/[^+\d]+/g, ''),
    } as FindApprovalInfo;

    this.customer.phone = phoneNumber;

    try {
      const response = await findApproval(approvalSearchInfo);

      this.application = response;
      this.currentView = this.getResumedPage(response.status);
    } catch (error) {
      if (error instanceof StatusCodeError) {
        if (error.statusCode === ErrorCodes.NotFound) {
          this.errorHelper.hasUnableToFindLeaseError = true;

          return;
        }
        this.errorHelper.hasGenericError = true;
      }
    } finally {
      this.showLoadingDialog = false;
    }
  }

  sendButtonClick(view: string, label: string) {
    pcDataLayer.gtmButtonClick({
      view,
      customerId: this.checkoutData?.customer?.id,
      leaseId: this.application?.leaseId,
      merchantName: this.retailerConfig?.merchantDisplayName,
      storeId: this.checkoutData?.merchant?.key,
      parentStoreId: this.retailerConfig?.parentStoreId,
      label: view === 'toc' && label === 'continue' ? 'start application' : label,
    });
  }

  sendGtmPageView(view: string) {
    pcDataLayer.gtmPageView({
      view,
      customerId: this.checkoutData?.customer?.id,
      leaseId: this.application?.leaseId,
      merchantName: this.retailerConfig?.merchantDisplayName,
      storeId: this.checkoutData?.merchant?.key,
      parentStoreId: this.retailerConfig?.parentStoreId,
    });
  }

  private handleOnFailure(message: string) {
    const errorMessage = { message, ...(this.application.leaseId && { id: this.application.leaseId }) } as FailureData;

    this.checkoutData.callbacks?.onFailure(errorMessage);
  }

  private getResumedPage(status: CreateApplicationReturn['status']) {
    switch (status) {
      case 'APP_APPROVED':
        if (this.checkoutData?.merchant?.flowType === 'applyOnly') {
          return PageKey.applyOnlyApproval;
        }

        return PageKey.approved;

      case 'APP_PENDING':
        return PageKey.pending;

      case 'APP_DENIED':
        return PageKey.denied;

      case 'FIND_VERIFY':
        return PageKey.verifyItsYou;

      case 'APP_SUBMIT_FAILURE':
        this.errorHelper.hasUnableSubmitFailureCase = true;

        return PageKey.disclosures;

      default:
        this.errorHelper.hasGenericError = true;

        return this.currentView;
    }
  }

  async stepForward() {
    this.errorHelper.resetErrorFlags();

    if (this.redirectToReview) {
      this.redirectToReview = false;
      this.currentView = PageKey.review;

      return;
    }

    const nextView = GetNextPage(this.currentView);

    if (nextView === PageKey.contactInfo) {
      try {
        this.isLoading = true;
        this.customer.tosAgree = true;
        await updateLeaseApplication({ tosAgree: true });
        pcDataLayer.gtmApplicationStart({ reapply: false });
      } catch (error) {
        if (error instanceof GenericAPIError) {
          this.errorHelper.hasGenericError = true;
        } else {
          this.customer.tosAgree = false;
        }

        return;
      } finally {
        this.isLoading = false;
      }
    }

    if (nextView !== PageKey.none) {
      this.currentView = nextView;
    }
  }

  checkValidState(shipState: string): boolean {
    if (shipState === 'undefined') {
      return false;
    }

    return this.retailerConfig?.leaseProhibitedInStates?.includes(shipState);
  }

  render() {
    const viewsObject: { [key in PageKey]: unknown } = {
      [PageKey.landing]: <pc-landing translation={this.translations?.landing} />,

      [PageKey.onboarding]: this.applyOnly ? (
        <pc-ao-landing
          leaseTerm={this.retailerConfig.overrideLeaseTermMonths}
          earlyBuyOut={this.retailerConfig.overrideBuyoutDays}
          translation={this.translations?.applyOnlyLanding}
        />
      ) : (
        <pc-onboarding
          merchantName={this.retailerConfig.merchantDisplayName}
          brand-logo={this.retailerConfig.merchantLogoUrl ? this.retailerConfig.merchantLogoUrl : this.retailerConfig.defaultLogoUrl}
          hideCobranding={this.retailerConfig.hideCobranding}
          blockedStates={this.retailerConfig.leaseProhibitedInStates}
          stateValidationError={this.errorHelper.hasStateValidError}
          translation={this.translations?.onboarding}
        />
      ),
      [PageKey.toc]: this.applyOnly ? (
        <pc-ao-before-apply
          conditions={JSON.stringify(getConditions(this.retailerConfig.overrideLeaseTermMonths, this.retailerConfig.leaseProhibitedInStates))}
          tosAgree={this.customer?.tosAgree}
          isLoading={this.isLoading}
          translation={this.translations?.beforeApplyOnly}
          errors={this.errorHelper}
        />
      ) : (
        <pc-terms-and-conditions
          conditions={JSON.stringify(getConditions(this.retailerConfig.overrideLeaseTermMonths, this.retailerConfig.leaseProhibitedInStates))}
          tosAgree={this.customer?.tosAgree}
          isLoading={this.isLoading}
          translation={this.translations?.toc}
          errors={this.errorHelper}
        />
      ),
      [PageKey.contactInfo]: <contact-info-state contactInfo={{ ...this.customer }} errors={this.errorHelper} />,

      [PageKey.personalInfo]: (
        <personal-info-state
          personalInfo={{ ...this.customer }}
          addressInfo={{ ...this.customer.address?.shipping }}
          addressNotPrefilled={this.addressNotPrefilled}
          redirectToReview={this.redirectToReview}
          errors={this.errorHelper}
        />
      ),

      [PageKey.addressInfo]: (
        <address-info-state addressInfo={{ ...this.customer.address }} errors={this.errorHelper} statesProhibited={this.retailerConfig.leaseProhibitedInStates} />
      ),

      [PageKey.incomeInfo]: <income-info-state incomeInfo={{ ...this.customer.incomeInfo }} errors={this.errorHelper} />,

      [PageKey.cardInfo]: (
        <card-info-state
          cardInfo={{ ...this.customer.cardInfo }}
          addressInfo={{ ...this.customer.address }}
          errors={this.errorHelper}
          blockedStatesCardInfo={this.retailerConfig.leaseProhibitedInStates}
        />
      ),

      [PageKey.bankInfo]: <bank-info-state bankInfo={{ ...this.customer.bankInfo }} errors={this.errorHelper} />,

      [PageKey.addCard]: (
        <add-card-info-state
          billingInfo={{ ...this.customer.address?.billing }}
          cardInfo={{ ...this.customer.cardInfo }}
          errors={this.errorHelper}
          blockedStatesAddCard={this.retailerConfig.leaseProhibitedInStates}
        />
      ),

      [PageKey.review]: <review-state customer={this.customer} translation={this.translations?.review} />,

      [PageKey.disclosures]: (
        <pc-disclosures showUnableSubmitFailureCase={this.errorHelper.hasUnableSubmitFailureCase} translation={this.translations?.disclosures}>
          {this.errorHelper.hasGenericError && <pc-generic-error slot="error-card" />}
        </pc-disclosures>
      ),

      [PageKey.approved]: (
        <pc-approved
          approvalLimit={this.application?.approvalLimit}
          validDate={getDateLong(this.application?.validThrough)}
          leaseTerm={this.retailerConfig.overrideLeaseTermMonths}
          translation={this.translations?.approved}
          errors={this.errorHelper}
          leaseId={this.application?.leaseId}
        />
      ),
      [PageKey.pending]: (
        <pc-pending
          expDate={getDateLong(this.application?.validThrough)}
          merchantName={this.retailerConfig.merchantDisplayName}
          translation={this.translations?.pending}
          leaseId={this.application?.leaseId}
        />
      ),
      [PageKey.denied]: <pc-denied merchantName={this.retailerConfig.merchantDisplayName} translation={this.translations?.denied} leaseId={this.application?.leaseId} />,
      [PageKey.applyOnlyApproval]: (
        <pc-ao-approved
          approvalLimit={this.application?.approvalLimit}
          validDate={getDateLong(this.application?.validThrough)}
          leaseTerm={this.retailerConfig.overrideLeaseTermMonths}
          translation={this.translations?.applyOnlyApproval}
          leaseId={this.application?.leaseId}
        />
      ),

      [PageKey.cartReview]: (
        <cart-review-state
          cartResponse={this.cartResponse}
          approvalLimit={this.application?.approvalLimit}
          retailerConfig={this.retailerConfig}
          deliveryCost={this.checkoutData?.cart?.deliveryCost}
        />
      ),

      [PageKey.wirelessPlan]: (
        <pc-wireless-plan
          items={JSON.stringify(this.merchantBilledItems)}
          cardNumberLastFour={this.customer.cardInfo?.cardNumber.slice(-numCCShown) || this.application?.creditCardLastFour}
          retailer={this.retailerConfig.merchantDisplayName}
          fallbackImage={this.retailerConfig.merchantLogoUrl ? this.retailerConfig.merchantLogoUrl : this.retailerConfig.defaultLogoUrl}
          translation={this.translations?.wirelessPlan}
        />
      ),

      [PageKey.esign]: <pc-esign esignUrl={this.esignUrl} showBack={false} translation={this.translations?.esign} />,

      [PageKey.checkoutCard]: (
        <pc-checkout-card
          {...this.vCard}
          merchantName={this.retailerConfig.merchantDisplayName}
          name={this.vCard?.name}
          showCheckoutError={this.errorHelper.hasUnableCheckoutFailureCase}
          translation={this.translations?.checkoutCard}
          buttonTextOverride={
            this.customer.preference.language === 'es'
              ? this.configUIData?.['virtual-card-checkout']?.buttonText?.spanish
              : this.configUIData?.['virtual-card-checkout']?.buttonText?.english
          }
        >
          {this.errorHelper.hasGenericError && <pc-generic-error slot="error-card" />}
        </pc-checkout-card>
      ),

      [PageKey.paymentSigning]: (
        <payment-signing-state
          cartResponse={this.cartResponse}
          cardInfo={this.customer.cardInfo}
          errors={this.errorHelper}
          leaseId={this.application?.leaseId}
          retailerConfig={this.retailerConfig}
          creditCardLastFour={this.application?.creditCardLastFour}
        />
      ),

      [PageKey.useMyApproval]: (
        <pc-use-approval
          showUnableToFindLeaseError={this.errorHelper.hasUnableToFindLeaseError || this.errorHelper.hasGenericError}
          translation={this.translations?.useMyApproval}
        />
      ),

      [PageKey.needHelp]: <pc-need-help translation={this.translations?.needHelp} />,

      [PageKey.verifyItsYou]: <validate-phone-state phone={this.customer.phone} errors={this.errorHelper} />,

      [PageKey.none]: null,
    };

    if (this.showLoadingDialog) {
      return <pc-loading />;
    }

    if (this.showExitDialog) {
      return <pc-exit-prompt translation={this.translations?.exitPrompt} />;
    }

    return viewsObject[this.currentView];
  }
}
